import { useEffect, useRef, useState } from 'react';
import { datadogLogs } from '@datadog/browser-logs';
import { Box, Switch, Typography } from '@coolblue-development/freddy';
import {
  NOISE_SUPPRESSION_OFF,
  NOISE_SUPPRESSION_ON,
  saveNoiseSuppression
} from '../../../../utils/voiceEnhancementStorage';

// `onVoiceEnhancementModeChanged` exists at runtime (amazon-connect-streams 2.26+) but is missing from its typings.
type AgentWithVoiceEvents = connect.Agent & {
  onVoiceEnhancementModeChanged: (
    callback: (change: { voiceEnhancementMode: connect.VoiceEnhancementMode }) => void
  ) => { unsubscribe: () => void };
};

const VoiceEnhancementToggle = (): JSX.Element => {
  const [enabled, setEnabled] = useState(false);
  const [ready, setReady] = useState(false);
  const agentRef = useRef<connect.Agent | null>(null);
  const latestRequestRef = useRef(0);
  const pendingUpdateRef = useRef<Promise<void>>(Promise.resolve());
  // The last state the agent confirmed, which a failed update reverts to.
  const confirmedRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let subscription: { unsubscribe: () => void } | undefined;

    const agentSubscription = connect.agent(agent => {
      if (cancelled) return;
      agentRef.current = agent;

      try {
        confirmedRef.current = agent.getVoiceEnhancementMode() === NOISE_SUPPRESSION_ON;
        setEnabled(confirmedRef.current);
        subscription = (agent as AgentWithVoiceEvents).onVoiceEnhancementModeChanged(({ voiceEnhancementMode }) => {
          confirmedRef.current = voiceEnhancementMode === NOISE_SUPPRESSION_ON;
          setEnabled(confirmedRef.current);
        });
      } catch (err) {
        datadogLogs.logger.warn('Unable to read voice enhancement mode', {}, err as Error);
      }
      setReady(true);
    });

    return () => {
      cancelled = true;
      agentRef.current = null;
      subscription?.unsubscribe();
      agentSubscription?.unsubscribe();
    };
  }, []);

  const handleChange = (checked: boolean): void => {
    const agent = agentRef.current;
    if (!agent) return;

    const requestId = ++latestRequestRef.current;
    setEnabled(checked);

    // Run updates one after the other so the agent always ends up in the last mode that was chosen.
    pendingUpdateRef.current = pendingUpdateRef.current.then(async () => {
      const isLatest = (): boolean => requestId === latestRequestRef.current;
      if (!isLatest()) return; // a newer choice supersedes this one

      try {
        await agent.setVoiceEnhancementMode(checked ? NOISE_SUPPRESSION_ON : NOISE_SUPPRESSION_OFF);
        confirmedRef.current = checked;
        if (isLatest()) saveNoiseSuppression(checked);
      } catch (err) {
        datadogLogs.logger.error('Unable to set voice enhancement mode', { voiceEnhancement: true }, err as Error);
        if (isLatest()) setEnabled(confirmedRef.current);
      }
    });
  };

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}>
      <Box>
        <Typography fontWeight={600}>Noise suppression</Typography>
        <Typography variant="caption">
          Filters out background noise from your microphone during voice calls. Takes effect on your next call.
        </Typography>
      </Box>
      <Switch
        aria-label="Noise suppression"
        data-testid="voice-enhancement-switch"
        checked={enabled}
        disabled={!ready}
        onChange={handleChange}
      />
    </Box>
  );
};

export default VoiceEnhancementToggle;
